import { parse, stringify } from 'qs';
import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isNewWebSearchHistoryEnabled } from 'reactApp/appHelpers/featuresHelpers';
import { useFallbackSearchUrl } from 'reactApp/hooks/useFallbackSearchUrl';
import { historyPush } from 'reactApp/modules/router/router.module';
import { sendSearchInputClickRadar } from 'reactApp/modules/search/search.analytics';
import { getSearchQueryString } from 'reactApp/modules/search/search.helpers';
import { searchLoadHistory } from 'reactApp/modules/search/search.module';
import { getSearchHistory } from 'reactApp/modules/search/search.selectors';
import { setQueryParams } from 'reactApp/modules/settings/settings.module';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { getIdByStorage, getStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import opener from 'reactApp/utils/opener';

import { EActions, IAction, ISearchContent, ISearchContentProps, IState, typesOptions } from '../WebSearch.data';

const reducer = (state: IState, action: IAction): IState => {
    switch (action.type) {
        case EActions.close:
            return {
                active: false,
                focus: false,
                open: false,
                value: '',
                type: 'all',
            };
        case EActions.removeType:
            return {
                ...state,
                type: 'all',
                open: false,
                focus: false,
            };
        case EActions.setFocus:
            return {
                ...state,
                open: false,
                focus: Boolean(action.payload),
            };
        case EActions.setType:
            return {
                ...state,
                open: false,
                type: String(action.payload),
            };
        case EActions.setActive:
            return {
                ...state,
                active: true,
                focus: true,
            };
        case EActions.setValue:
            return {
                ...state,
                open: false,
                value: String(action.payload),
            };
        case EActions.setOpenFilters:
            return {
                ...state,
                open: Boolean(action.payload),
            };
        case EActions.toggleFilters:
            return {
                ...state,
                focus: false,
                open: !state.open,
            };
        case EActions.searchValue:
            return {
                ...state,
                focus: false,
                open: false,
                value: String(action.payload),
                type: 'all',
            };
    }
};

// eslint-disable-next-line max-lines-per-function,sonarjs/cognitive-complexity
export const useWebSearchContent = ({
    toggleSearch,
    isExpanded,
    onlyExpanded,
    storage,
    onSearchStateChange,
}: ISearchContentProps): ISearchContent => {
    const { isAttaches, isReactPage, isSearch } = getStorage(storage);

    const historyItems = useSelector(getSearchHistory);
    const { query = '', searchType = 'all' } = useSelector(SettingsSelectors.getQueryParams) || {};
    const { setStartLocation, closeSearch } = useFallbackSearchUrl();

    const [state, invokeDispatch] = useReducer(reducer, {
        active: Boolean(onlyExpanded),
        focus: false,
        open: false,
        value: query,
        type: searchType,
    });

    const dispatch = ({ type, payload }: { type: EActions; payload?: any }) => {
        onSearchStateChange({ type, payload });
        invokeDispatch({ type, payload });
    };

    const storeDispatch = useDispatch();

    const placeholder = isAttaches ? 'Поиск по разделу' : 'Поиск по Облаку';
    const searchOption = typesOptions[state.type];

    const showSearchPage = useCallback(
        ({ value, type }: { value: string; type: string }) => {
            if (!value && type === 'all') {
                return;
            }

            const { query: _query, searchType: _, ...rest } = parse(window.location.search, { ignoreQueryPrefix: true });
            const searchOther = stringify(rest);
            const search = getSearchQueryString(value, type);

            if (!isSearch) {
                setStartLocation();
            }

            if (isAttaches) {
                storeDispatch(historyPush({ id: '/attaches', search: `${search}${searchOther ? `&${searchOther}` : ''}` }));
                return;
            }

            if (isReactPage) {
                const queryString = stringify({ query: value, searchType: type, ...rest }, { addQueryPrefix: true });
                // setQueryParams вызываем чтобы компонент SearchPage сразу получал query (иначе сначала query=undefined только потом в стор проставляется значение)
                const searchRequestParams = parse(queryString, { ignoreQueryPrefix: true });

                storeDispatch(setQueryParams(searchRequestParams));
                storeDispatch(historyPush({ id: '/search/', search: `${search}${searchOther ? `&${searchOther}` : ''}` }));
            }
        },
        [isAttaches, isReactPage, isSearch, setStartLocation, storeDispatch]
    );

    const onInputClick = useCallback(() => {
        if (isExpanded || onlyExpanded) {
            dispatch({ type: EActions.setFocus, payload: true });
        } else {
            toggleSearch(true);
            sendSearchInputClickRadar({ storage });
        }
    }, [isExpanded, onlyExpanded, storage, toggleSearch]);

    const onSearchClick = useCallback(() => {
        if (state.active) {
            dispatch({ type: EActions.setFocus, payload: false });
            showSearchPage({ value: state.value, type: state.type });
            return;
        }

        toggleSearch(true);
    }, [showSearchPage, state.active, state.value, state.type, toggleSearch]);

    const onInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        dispatch({ type: EActions.setValue, payload: event.target?.value });
    }, []);

    const onInputClear = useCallback(() => {
        if (state.value) {
            dispatch({ type: EActions.setValue, payload: '' });
            return;
        }

        dispatch({ type: EActions.removeType });

        if (isAttaches) {
            storeDispatch(historyPush({ id: getIdByStorage(EStorageType.attaches) }));
            toggleSearch(false);
            return;
        }

        if (isReactPage) {
            closeSearch();
            toggleSearch(false);
            return;
        }

        // TODO: поправить после https://jira.vk.team/browse/CLOUDWEB-13172
        opener(getIdByStorage(EStorageType.home), true);
    }, [state.value, dispatch, isAttaches, isReactPage, storeDispatch, toggleSearch, closeSearch]);

    const onInputBlur = useCallback(() => {
        if (!state.open && !historyItems.length) {
            dispatch({ type: EActions.setFocus, payload: false });
        }
    }, [state.open, historyItems.length]);

    const onClickAway = useCallback(() => {
        dispatch({ type: EActions.setFocus, payload: false });

        if (!onlyExpanded) {
            toggleSearch(false);
        }
    }, [onlyExpanded, toggleSearch]);

    const setActiveInput = useCallback(() => {
        dispatch({ type: EActions.setActive });
    }, []);

    const chooseSearchType = useCallback((type: string) => {
        if (typesOptions[type]) {
            dispatch({ type: EActions.setType, payload: type });
            return;
        }

        dispatch({ type: EActions.setOpenFilters, payload: false });
    }, []);

    const removeSearchType = useCallback(() => {
        dispatch({ type: EActions.removeType });
    }, []);

    const removeFocus = useCallback(() => {
        dispatch({ type: EActions.setFocus, payload: false });
    }, []);

    const onKeyUp = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                dispatch({ type: EActions.setFocus, payload: false });
                showSearchPage({ value: state.value, type: state.type });
            }
        },
        [state.value, state.type, showSearchPage]
    );

    const searchValue = useCallback(
        (value: string) => {
            dispatch({ type: EActions.searchValue, payload: value });
            showSearchPage({ value, type: 'all ' });
        },
        [showSearchPage]
    );

    const onToggleFilters = useCallback(() => {
        dispatch({ type: EActions.toggleFilters });
    }, []);

    useEffect(() => {
        if (!isExpanded) {
            dispatch({ type: EActions.close });
        }
    }, [isExpanded]);

    useEffect(() => {
        showSearchPage({ value: state.value, type: state.type });
    }, [state.type]);

    useEffect(() => {
        if (state.focus && isNewWebSearchHistoryEnabled) {
            storeDispatch(searchLoadHistory());
        }
    }, [state.focus]);

    return {
        isFocus: state.focus,
        isOpen: state.open,
        value: state.value,
        searchOption,
        placeholder,
        onSearchClick,
        onInputClick,
        onInputClear,
        onClickAway,
        onInputChange,
        onInputBlur,
        onKeyUp,
        onToggleFilters,
        setActiveInput,
        searchValue,
        chooseSearchType,
        removeSearchType,
        removeFocus,
        historyItems,
    };
};
